import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';

export const Navbar = () => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: t('navbar.product'), path: '/product' },
    { name: t('navbar.pricing'), path: '/pricing' },
    { name: t('navbar.docs'), path: '/docs' },
    { name: t('navbar.company'), path: '/company' },
  ];

  return (
    <nav
      className={cn(
        "fixed top-0 left-0 w-full z-50 transition-all duration-300 border-b border-transparent",
        isScrolled ? "bg-deep-black/80 backdrop-blur-md border-glass-border py-4" : "bg-transparent py-6"
      )}
    >
      <div className="container mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group">
          <div className="relative w-8 h-8 flex items-center justify-center">
            <div className="absolute inset-0 border border-glass-cyan rotate-45 transition-transform group-hover:rotate-90" />
            <div className="w-2 h-2 bg-glass-cyan rounded-full shadow-[0_0_10px_#00f0ff]" />
          </div>
          <span className="font-display font-bold text-xl tracking-wider text-white">
            INFINITY <span className="text-glass-cyan">BLUE</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className="text-sm font-medium text-gray-400 hover:text-glass-cyan transition-colors tracking-wide uppercase"
            >
              {link.name}
            </Link>
          ))}
        </div>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-4">
          <Link to="/login" className="text-sm font-medium text-white hover:text-glass-cyan transition-colors">
            {t('navbar.signIn')}
          </Link>
          <button className="glass-button-primary text-sm py-2 px-5">
            {t('navbar.startTrial')}
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-white"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X /> : <Menu />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-deep-black border-b border-glass-border p-6 flex flex-col gap-4">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className="text-lg font-medium text-gray-300 hover:text-glass-cyan"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {link.name}
            </Link>
          ))}
          <div className="h-[1px] bg-glass-border my-2" />
          <Link to="/login" className="text-lg font-medium text-white">{t('navbar.signIn')}</Link>
          <button className="glass-button-primary w-full">{t('navbar.startTrial')}</button>
        </div>
      )}
    </nav>
  );
};
